/**
 * The sales ledger's invoicing settings on a customer's Settings page
 * (docs/features/credit-control/06_ui_strategy.md §2; order to invoice doc 50
 * §4.1, §9), drawn where the design puts them:
 * - `CreditAccountInvoicing`, nested in the credit account block: when
 *   made-to-order work is invoiced, the invoice grouping, the cut-off with its
 *   day (order to invoice doc 109) and postage. Shown while credit account is
 *   ticked; card and pro-forma orders are invoiced when paid. Switching credit
 *   account off keeps them, dormant (display only).
 * - `StockBillingSection`, for every payer: stock billing, and whether it
 *   posts on its own carrier account.
 *
 * Each setting shows its own last change ("The default; never changed." until
 * someone changes it). Edits live in the page's draft (`useInvoicingDraft`)
 * and are saved with the page's one Save. A refused save's field errors are
 * bound to their inputs (api-validation.md). Finance, managers and admins
 * edit (the read says, `canManage`); everyone else reads, with who changed
 * each setting and when. Every label and option is the backend's.
 */
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import type {
  InvoicingChoiceSetting,
  InvoicingSettingChange,
  InvoicingSettingsValues,
} from "@shared-core/data/sales-ledger-types";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Checkbox } from "../../primitives/checkbox";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import type { PaymentSaveError } from "./useSaveSettings";
import type { InvoicingDraft } from "./useSettingsDrafts";

/** The request body's field names (what a refused save's errors name) for each editable setting. */
const FIELD_NAMES: Record<keyof InvoicingSettingsValues, string> = {
  madeToOrderInvoiceTrigger: "made_to_order_invoice_trigger",
  postageInvoicing: "postage_invoicing",
  stockBilling: "stock_billing",
  postsOnOwnCarrierAccount: "posts_on_own_carrier_account",
  invoiceGrouping: "invoice_grouping",
  invoiceCutOff: "invoice_cut_off",
  invoiceCutOffDay: "invoice_cut_off_day",
};

const errorId = (key: keyof InvoicingSettingsValues) =>
  `invoicing-${FIELD_NAMES[key]}-error`;

/** The refused save's messages for one setting, or undefined. */
function fieldMessage(
  error: PaymentSaveError | null,
  key: keyof InvoicingSettingsValues,
): string | undefined {
  const messages = (error?.fieldErrors ?? [])
    .filter((e) => e.field === FIELD_NAMES[key])
    .map((e) => e.message);
  return messages.length > 0 ? messages.join(" ") : undefined;
}

/** "The default; never changed." or "Changed by Lisa S. on 12 Oct 2026, 10:02: “Terms agreed at review”". */
function lastChangeText(
  isDefault: boolean,
  change: InvoicingSettingChange | null,
): string {
  if (isDefault || !change) return "The default; never changed.";
  return `Changed by ${change.byName ?? "a former user"} on ${formatDateTimeOrDash(change.at)}: “${change.reason}”`;
}

interface SectionProps {
  draft: InvoicingDraft;
  /** The page's Save is in flight: nothing here edits meanwhile. */
  saving: boolean;
  /** Why the invoicing part of the last Save was refused, shown with the fields. */
  error: PaymentSaveError | null;
}

/** The days a cut-off can fall on: weekdays, then days of the month and the last day (the backend's options). */
const WEEKDAY_VALUES = new Set([
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
]);

type ChoiceKey =
  | "madeToOrderInvoiceTrigger"
  | "postageInvoicing"
  | "stockBilling"
  | "invoiceGrouping"
  | "invoiceCutOff";

/**
 * The cut-off's day, chosen from the backend's options for the cut-off: weekdays for Weekly, the days of the month
 * and the last day for Monthly. The page fills it when a cut-off is chosen (the draft); the backend still refuses a
 * day that does not suit the cut-off, shown under the field. Drawn only for Weekly and Monthly (When due has none).
 */
function CutOffDayField({
  setting,
  cutOff,
  draft,
  disabled,
  error,
}: {
  setting: InvoicingChoiceSetting<string>;
  cutOff: "weekly" | "monthly";
  draft: InvoicingDraft;
  disabled: boolean;
  error: PaymentSaveError | null;
}) {
  const message = fieldMessage(error, "invoiceCutOffDay");
  const inputId = "invoicing-invoice_cut_off_day";
  const options = setting.options.filter(
    (o) => WEEKDAY_VALUES.has(o.value) === (cutOff === "weekly"),
  );
  return (
    <div className="space-y-1">
      <Label htmlFor={inputId} className="font-medium">
        {setting.label}
      </Label>
      <select
        id={inputId}
        value={draft.values?.invoiceCutOffDay ?? setting.value}
        disabled={disabled}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? errorId("invoiceCutOffDay") : undefined}
        onChange={(event) => draft.set("invoiceCutOffDay", event.target.value)}
        className="block h-9 w-full max-w-xs rounded-md border border-input bg-input-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {message && (
        <p
          id={errorId("invoiceCutOffDay")}
          className="text-xs text-destructive"
        >
          {message}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {lastChangeText(setting.isDefault, setting.lastChange)}
      </p>
    </div>
  );
}

function ChoiceField<K extends ChoiceKey>({
  settingKey,
  setting,
  draft,
  disabled,
  error,
  legendHidden = false,
}: {
  settingKey: K;
  setting: InvoicingChoiceSetting<InvoicingSettingsValues[K]>;
  draft: InvoicingDraft;
  disabled: boolean;
  error: PaymentSaveError | null;
  /** The section's own heading already says it: the legend stays for screen readers only. */
  legendHidden?: boolean;
}) {
  const labelId = `invoicing-${FIELD_NAMES[settingKey]}-label`;
  const message = fieldMessage(error, settingKey);
  return (
    <fieldset className="space-y-1" disabled={disabled}>
      <legend id={labelId} className={legendHidden ? "sr-only" : "font-medium"}>
        {setting.label}
      </legend>
      <RadioGroup
        value={draft.values?.[settingKey] ?? setting.value}
        onValueChange={(value) =>
          draft.set(settingKey, value as InvoicingSettingsValues[K])
        }
        aria-labelledby={labelId}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? errorId(settingKey) : undefined}
        disabled={disabled}
        className="flex flex-wrap gap-x-4 gap-y-2"
      >
        {setting.options.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <RadioGroupItem
              value={option.value}
              id={`invoicing-${FIELD_NAMES[settingKey]}-${option.value}`}
            />
            <Label
              htmlFor={`invoicing-${FIELD_NAMES[settingKey]}-${option.value}`}
              className="font-normal"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
      {message && (
        <p id={errorId(settingKey)} className="text-xs text-destructive">
          {message}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {lastChangeText(setting.isDefault, setting.lastChange)}
      </p>
    </fieldset>
  );
}

/**
 * A refused invoicing save, said once for screen readers and for what binds
 * to no input (the reason, nothing to change): in the section every payer
 * has, while the fields it names are marked where they are.
 */
function RefusedSave({ error }: { error: PaymentSaveError | null }) {
  if (!error) return null;
  const count = error.fieldErrors.length;
  return (
    <Alert variant="destructive" role="alert">
      <AlertDescription>
        {count > 0
          ? `Invoicing settings not saved: ${count} ${count === 1 ? "problem" : "problems"}. ${error.message}`
          : error.message}
      </AlertDescription>
    </Alert>
  );
}

/**
 * "Invoicing on credit account", inside the credit account block: the
 * made-to-order invoice trigger, the grouping (shown, one value) and postage.
 */
export function CreditAccountInvoicing({ draft, saving, error }: SectionProps) {
  const invoicing = draft.invoicing;
  if (!invoicing) return null;
  const disabled = saving || !invoicing.canManage;
  const cutOff = draft.values?.invoiceCutOff ?? invoicing.invoiceCutOff.value;
  // A box under its heading, deliberately not a labelled region: a label
  // containing "credit account" would also answer for the Credit account
  // checkbox's own label to anything finding it by label text.
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <h3 className="font-medium">Invoicing on credit account</h3>
      <ChoiceField
        settingKey="madeToOrderInvoiceTrigger"
        setting={invoicing.madeToOrderInvoiceTrigger}
        draft={draft}
        disabled={disabled}
        error={error}
      />
      <ChoiceField
        settingKey="invoiceGrouping"
        setting={invoicing.invoiceGrouping}
        draft={draft}
        disabled={disabled}
        error={error}
      />
      <ChoiceField
        settingKey="invoiceCutOff"
        setting={invoicing.invoiceCutOff}
        draft={draft}
        disabled={disabled}
        error={error}
      />
      {cutOff !== "when_due" && (
        <CutOffDayField
          setting={invoicing.invoiceCutOffDay}
          cutOff={cutOff}
          draft={draft}
          disabled={disabled}
          error={error}
        />
      )}
      <ChoiceField
        settingKey="postageInvoicing"
        setting={invoicing.postageInvoicing}
        draft={draft}
        disabled={disabled}
        error={error}
      />
    </div>
  );
}

/** "Stock billing", for every payer: how stock held for it is billed, and whether it posts on its own carrier account. */
export function StockBillingSection({ draft, saving, error }: SectionProps) {
  const invoicing = draft.invoicing;
  if (!invoicing) return null;
  const disabled = saving || !invoicing.canManage;
  const carrier = invoicing.postsOnOwnCarrierAccount;
  const carrierMessage = fieldMessage(error, "postsOnOwnCarrierAccount");
  return (
    <section
      aria-labelledby="stock-billing-heading"
      className="space-y-4 rounded-lg border border-border p-4 text-sm"
    >
      <h2 id="stock-billing-heading" className="text-base font-medium">
        {invoicing.stockBilling.label}
      </h2>
      <RefusedSave error={error} />
      <ChoiceField
        settingKey="stockBilling"
        setting={invoicing.stockBilling}
        draft={draft}
        disabled={disabled}
        error={error}
        legendHidden
      />
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="invoicing-posts-on-own-carrier-account"
            checked={draft.values?.postsOnOwnCarrierAccount ?? carrier.value}
            disabled={disabled}
            aria-invalid={carrierMessage ? true : undefined}
            aria-describedby={
              carrierMessage ? errorId("postsOnOwnCarrierAccount") : undefined
            }
            onCheckedChange={(checked) =>
              draft.set("postsOnOwnCarrierAccount", checked === true)
            }
          />
          <Label
            htmlFor="invoicing-posts-on-own-carrier-account"
            className="font-normal"
          >
            {carrier.label}
          </Label>
        </div>
        {carrierMessage && (
          <p
            id={errorId("postsOnOwnCarrierAccount")}
            className="text-xs text-destructive"
          >
            {carrierMessage}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {lastChangeText(carrier.isDefault, carrier.lastChange)}
        </p>
      </div>
    </section>
  );
}
